import React from 'react'
import { useTranslation } from 'react-i18next'
import { FiCreditCard } from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useTheme } from '../../context/ThemeContext'

// Renders the top-level payment buckets (card-or-bank, MobilePay, PayPal) as
// selectable cards. Intentionally renders nothing when there is one bucket or
// fewer — the caller auto-selects and shows a single inline payment form.
//
// Generic by design: it only knows bucket descriptors (id, kind, labelKey,
// brandSubtitle), not Stripe/PayPal specifics.
function BucketIcon({ bucket, color }) {
  if (bucket.id === 'paypal') {
    return (
      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#FFF5E0]">
        <span className="text-xl font-bold text-[#0070BA]">P</span>
      </div>
    )
  }
  if (bucket.id === 'mobilepay') {
    return (
      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#EAF3FF]">
        <span className="text-sm font-bold text-[#5A78FF]">MP</span>
      </div>
    )
  }
  return (
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: `${color}15` }}
    >
      <SafeIcon icon={FiCreditCard} className="w-6 h-6" style={{ color }} />
    </div>
  )
}

export default function PaymentMethodSelector({ buckets, selectedId, onSelect, disabled = false }) {
  const { t } = useTranslation()
  const theme = useTheme()

  if (!buckets || buckets.length <= 1) return null

  return (
    <div className="space-y-3" role="radiogroup" aria-label={t('manage.choosePaymentMethod', 'Choose a payment method')}>
      {buckets.map(bucket => {
        const isSelected = bucket.id === selectedId
        const title = bucket.labelKey ? t(bucket.labelKey) : bucket.id
        const subtitle = bucket.brandSubtitle || ''
        return (
          <button
            key={bucket.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(bucket.id)}
            disabled={disabled}
            className="w-full bg-white rounded-2xl shadow-sm border p-5 text-left transition-colors hover:border-gray-300 disabled:opacity-50"
            style={{ borderColor: isSelected ? theme.colors.primary.base : '#f3f4f6' }}
          >
            <div className="flex items-center gap-4">
              <BucketIcon bucket={bucket} color={theme.colors.primary.base} />
              <div>
                <p className="font-semibold text-gray-900">{title}</p>
                {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
